import type {
  CNSPluginConnectionIntent,
  CNSPluginConnectionSurface,
} from "@/models/plugin";

export type ConnectorOAuthSurface =
  | "settings"
  | "source_picker"
  | "chat_connect_source"
  | "plugins";

export type ConnectorOAuthAttemptStatus =
  | "preparing"
  | "pending"
  | "exchanging"
  | "succeeded"
  | "failed"
  | "cancelled"
  | "expired";

export interface ConnectorOAuthStartContext {
  surface: ConnectorOAuthSurface;
  returnPath?: string;
  projectPublicId?: string;
  conversationPublicId?: string;
  toolCallId?: string;
  connectionPublicId?: string;
}

/** Server-normalized OAuth context returned by authorize/start/resume endpoints. */
export interface ConnectorOAuthResolvedContext {
  workspacePublicId: string;
  surface: ConnectorOAuthSurface;
  returnPath: string;
  connectionPublicId?: string;
  projectPublicId?: string;
  conversationPublicId?: string;
  toolCallId?: string;
  pluginKey?: string;
  connectionOptionKey?: string;
  connectionIntent?: CNSPluginConnectionIntent;
}

export interface ConnectorOAuthAuthorization {
  attemptPublicId: string;
  authorizationUrl: string;
  credentialPublicId: string;
  expiresAt: string;
  context: ConnectorOAuthResolvedContext;
}

export type ConnectorOAuthContinuationExecution = "none" | "running" | "completed";

export interface ConnectorOAuthAttempt {
  attemptPublicId: string;
  credentialPublicId: string;
  provider?: string | null;
  workspacePublicId?: string | null;
  status: ConnectorOAuthAttemptStatus;
  scope?: string | null;
  error?: string | null;
  errorCode?: string | null;
  toolResultApplied: boolean;
  toolResultAppliedAt?: string | null;
  continuationClaimedAt?: string | null;
  claimedAssistantMessageId?: string | null;
  continuationExecution?: ConnectorOAuthContinuationExecution | null;
  surface?: ConnectorOAuthSurface | null;
  returnPath?: string | null;
  projectPublicId?: string | null;
  conversationPublicId?: string | null;
  toolCallId?: string | null;
  connectionPublicId?: string | null;
  pluginKey?: string | null;
  connectionOptionKey?: string | null;
  connectionIntent?: CNSPluginConnectionIntent | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  expiresAt?: string | null;
  exchangeExpiresAt?: string | null;
}

export interface ConnectorOAuthLocalIntent extends ConnectorOAuthResolvedContext {
  attemptPublicId: string;
  credentialPublicId: string;
  createdAt: number;
}

export type ConnectorOAuthAutoContinuationState =
  | "none"
  | "eligible"
  | "started"
  | "suppressed";

export interface ConnectorOAuthAttemptEntry {
  attempt: ConnectorOAuthAttempt;
  startedInThisTab: boolean;
  lastConfirmedAt?: number;
  autoContinuationState: ConnectorOAuthAutoContinuationState;
}

/** Maps plugin connection surfaces to connector OAuth surfaces. */
export function mapPluginConnectionSurfaceToConnectorOAuthSurface(
  surface: CNSPluginConnectionSurface,
): ConnectorOAuthSurface {
  return surface;
}

/** Returns true when the backend attempt cannot transition back to an active status. */
export function isConnectorOAuthAttemptTerminal(
  status: ConnectorOAuthAttemptStatus,
): boolean {
  return (
    status === "succeeded" ||
    status === "failed" ||
    status === "cancelled" ||
    status === "expired"
  );
}
